import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Form, InputNumber, Row, Col, message, Rate } from 'antd';
import ButtonLink from 'components/theme_components/ButtonLink';
import FormInput from 'components/FormInput';
import ParListSelect from 'components/ParListSelect';
import RateFormulaSelectByCriteria from 'components/RateFormulaSelectByCriteria';

type ProductParticipationModalProps = {
  setVisible: (visible: boolean) => void;
  prefsteplist: any[];
  onSaveRate?: (rate: any) => void;
  isCreate?: boolean;
  data?: any;
  durationmax?: number;
  franchiseperiod?: number;
};

const ProductParticipationModal: React.FC<ProductParticipationModalProps> = ({
  setVisible,
  prefsteplist,
  onSaveRate,
  isCreate,
  data,
  durationmax = 0,
  franchiseperiod = 0,
}) => {
  const [form] = Form.useForm();
  const [disableNscFields, setDisableNscFields] = useState<boolean>(false);
  const [nscRateFormulaCode, setNscRateFormulaCode] = useState<string>('');
  const [nscRateFormulaLabel, setNscRateFormulaLabel] = useState<string>('');

  const updateEndDay = useCallback(() => {
    const values = form.getFieldsValue();
    if (values.ratestartday && values.ratenbdaysmax) {
      const endDay =
        Number(values.ratestartday) + Number(values.ratenbdaysmax) - 1;
      form.setFieldsValue({ rateendday: endDay });
    }
  }, [form]);

  Form.useWatch(['ratestartday', 'ratenbdaysmax'], form);
  const ratecodespread = form.getFieldValue('ratecodespread');

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const startDay = Number(values.ratestartday);
      const nbDays = Number(values.ratenbdaysmax);
      const endDay = startDay + nbDays - 1;

      // Get the actual values from the form
      const nscappliedrate = Number(form.getFieldValue('nscappliedrate') || 0);
      const clientappliedrate = Number(
        form.getFieldValue('clientappliedrate') || 0
      );
      const nscrateformulaValue = form.getFieldValue('nscrateformula');
      const rateformulaValue = form.getFieldValue('rateformula');
      const nscbillingcondition = form.getFieldValue('nscbillingcondition');
      const clibillingcondition = form.getFieldValue('clibillingcondition');

      if (endDay > Number(durationmax)) {
        message.warning(
          `La période dépasse la durée maximale du produit (${durationmax} jours)`
        );
        return;
      }
      if (isCreate) {
        let nextPeriodNumber = 1;
        if (prefsteplist.length > 0) {
          nextPeriodNumber =
            Math.max(
              ...prefsteplist.map((item) => Number(item.nsc?.ctesorder || 0))
            ) + 1;
        }

        const baseKey =
          prefsteplist.length === 0
            ? 1
            : Math.max(...prefsteplist.map((p) => p.nsc?.key || 0)) + 1;

        const nscStep = {
          key: baseKey,
          ctesorder: nextPeriodNumber,
          ratestartday: startDay,
          ratenbdaysmax: nbDays,
          rateendday: endDay,
        //  nscrateformula: nscrateformulaValue || 'N/A',
           nscrateformula: nscRateFormulaCode || 'N/A', 
  nscrateformulalabel: nscRateFormulaLabel || 'N/A', 
          nscappliedrate: nscappliedrate || 'N/A',
          clibillingcondition: null,
          nscbillingcondition: nscbillingcondition || null,
          rateformula: null,
          clientappliedrate: null,
          prefinsteptype: 'PART',
        };

        const clientStep = {
          key: baseKey + 1,
          ctesorder: nextPeriodNumber,
          ratestartday: startDay,
          ratenbdaysmax: nbDays,
          rateendday: endDay,
          nscrateformula: null,
          nscappliedrate: null,
          clibillingcondition: clibillingcondition,
          nscbillingcondition: clibillingcondition||"EOP",
          rateformula: rateformulaValue || 'N/A',
          clientappliedrate,
          prefinsteptype: 'INT',
        };

        console.log('Creating new rate period:', { nscStep, clientStep });
        onSaveRate?.([nscStep, clientStep]);
      } else {
        // Edit mode
        const periodNumber = data?.nsc?.ctesorder || 1;
        const baseKey = data?.nsc?.key || 1;

        const nscStep = {
          key: baseKey,
          ctesorder: periodNumber,
          ratestartday: startDay,
          ratenbdaysmax: nbDays,
          rateendday: endDay,
          nscrateformula: nscrateformulaValue || 'N/A',
          nscappliedrate: nscappliedrate || 'N/A',
          clibillingcondition: null,
          nscbillingcondition: nscbillingcondition || null,
          rateformula: null,
          clientappliedrate: null,
          prefinsteptype: 'PART',
        };

        const clientStep = {
          key: baseKey + 1,
          ctesorder: periodNumber,
          ratestartday: startDay,
          ratenbdaysmax: nbDays,
          rateendday: endDay,
          nscrateformula: null,
          nscappliedrate: null,
          clibillingcondition: clibillingcondition,
          nscbillingcondition: clibillingcondition||"EOP",
          rateformula: rateformulaValue || 'N/A',
          clientappliedrate,
          prefinsteptype: 'INT',
        };


        console.log('Updating rate period:', { nscStep, clientStep });
        onSaveRate?.([nscStep, clientStep]);
      }

      setVisible(false);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  useEffect(() => {
    form.resetFields();

    if (data) {
      const formValues = {
        ratestartday: data?.nsc?.ratestartday || 1,
        ratenbdaysmax: data?.nsc?.ratenbdaysmax || 1,
        rateendday: data?.nsc?.rateendday || 1,
       nscrateformula: data?.nsc?.nscrateformulalabel || data?.nsc?.nscrateformula || null,
        nscappliedrate: data?.nsc?.nscappliedrate || 0,
        nscbillingcondition: data?.nsc?.nscbillingcondition || '',
        clibillingcondition: data?.client?.clibillingcondition || 'EOP',
        rateformula: data?.client?.rateformula || null,
        clientappliedrate: data?.client?.clientappliedrate || 0,
      };

      console.log('Setting form values for edit:', formValues);
      form.setFieldsValue(formValues);

      setNscRateFormulaCode(data?.nsc?.nscrateformula || '');
setNscRateFormulaLabel(data?.nsc?.nscrateformulalabel || data?.nsc?.nscrateformula || '');

      const startDay = data?.nsc?.ratestartday || 1;
      const endDay = startDay + (data?.nsc?.ratenbdaysmax || 0) - 1;
      setDisableNscFields(endDay > franchiseperiod);
    } else if (isCreate) {
      // Create mode
      let startDay = 1;
      let availableDays = franchiseperiod || durationmax;

      if (prefsteplist.length > 0) {
        const lastItem = prefsteplist[prefsteplist.length - 1];
        const lastEndDay = Number(lastItem.nsc?.rateendday || 0);
        startDay = lastEndDay + 1;
         if (startDay > franchiseperiod) {
          availableDays = durationmax - lastEndDay;
        } else {
          availableDays = franchiseperiod - lastEndDay;
        }
      }

      form.setFieldsValue({
        ratestartday: startDay,
        ratenbdaysmax: availableDays > 0 ? availableDays : 1,
        rateendday: startDay + (availableDays > 0 ? availableDays : 1) - 1,
      });

      const endDay = startDay + (availableDays > 0 ? availableDays : 1) - 1;

      setDisableNscFields(endDay > franchiseperiod);
      
      setNscRateFormulaLabel('');
      
      console.log('disableFields:', disableNscFields)
    }

    updateEndDay();
  }, [
    data,
    isCreate,
    updateEndDay,
    prefsteplist,
    durationmax,
    franchiseperiod,
    form,
  ]);

  return (
    <Form
      form={form}
      layout="vertical"
      className="no-border-radius ut-wizard-form"
    >
      <Row gutter={16}>
        <Col span={8}>
          <FormInput
            name="ratestartday"
            rules={[
              { required: true, message: 'Veuillez saisir un jour de début' },
            ]}
          >
            <InputNumber
              disabled 
              min={1}
              className="no-border-radius ut-wizard-form color-gray"
            />
          </FormInput>
        </Col>
        <Col span={8}>
          <FormInput
            name="ratenbdaysmax"
            rules={[
              { required: true, message: 'Veuillez saisir le nombre de jours' },
              {
                validator: (_, value) => {
                  const currentRateNbDaysMax = Number(value);
                  const otherRatesSum = prefsteplist.reduce((sum, item) => {
                    if (data && item.nsc?.key === data.nsc?.key) {
                      return sum;
                    }
                    return sum + Number(item.nsc?.ratenbdaysmax || 0);
                  }, 0);

                  const totalDays = currentRateNbDaysMax + otherRatesSum;

                  if (totalDays > franchiseperiod) {
                    return Promise.reject(
                      new Error(
                        'Le nombre de jours maxi doit être inférieur à Le nombre de jours participations'
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}

          >
            <InputNumber
              min={1}
              max={durationmax}
              className="no-border-radius ut-wizard-form color-gray"
              onChange={updateEndDay}
            />
          </FormInput>
        </Col>
        <Col span={8}>
          <FormInput name="rateendday">
            <InputNumber
              disabled 
              className="no-border-radius ut-wizard-form color-gray"
            />
          </FormInput>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <FormInput
            name="nscbillingcondition"
            rules={[
              {
                required: !disableNscFields,
                message: 'Veuillez selectionner une condition',
              },
            ]}
          >
            <ParListSelect parcode="INTCALC" disabled={disableNscFields} />
          </FormInput>
        </Col>
        <Col span={12}>
          <FormInput
            name="nscrateformula"
            rules={[
              {
                required: !disableNscFields,
                message: 'Veuillez selectionner la formule nsc',
              },
            ]}
          >
            <RateFormulaSelectByCriteria
              disabled={disableNscFields}
              {...({displayValue: nscRateFormulaLabel} as any)}
              params={{
                formulalabel: '',
                formulacode: '',
                formulastatus: '',
                formulatype: '',
                ratecode: '',
                ratelabel: '',
                formuladescription: '',
                ...(ratecodespread !== undefined &&
                  ratecodespread !== '' &&
                  !isNaN(ratecodespread) && {
                    ratecodespread: Number(ratecodespread),
                  }),
              }}
         onSelected={(e) => {
  console.log('Selected NSC rate formula:', e);
 
  setNscRateFormulaCode(e.formulacode);
  setNscRateFormulaLabel(e.formulalabel || e.formulacode);
  
  form.setFieldsValue({
    nscappliedrate: e.ratevalue + (e?.ratespread || 0),
   
    nscrateformula: e.formulalabel || e.formulacode,
  });
}}
            />
          </FormInput>
        </Col>
      </Row>



      <Row justify="end" className="mt-7">
        <ButtonLink
          comcode="Annuler"
          className="ut-btn-bordred"
          onClick={() => setVisible(false)}
        />
        <ButtonLink
          type="primary"
          comcode="validate"
          htmlType="submit"
          className="ut-reset-btn"
          onClick={handleSubmit}
          iconPosition="end"
        />
      </Row>
    </Form>
  );
};

export default ProductParticipationModal;